import {
  Alert,
  Button,
  ButtonGroup,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { array, boolean, number, object, string } from "yup";
import { buildPermission } from "portal/utils/auth";
import { capitalize } from "portal/utils/strings";
import { CheckboxWithLabel, TextField } from "formik-mui";
import { classes } from "portal/theme/theme";
import {
  closestCorners,
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { CustomerSelector } from "../customers/CustomerSelector";
import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagForCustomer,
} from "portal/utils/hooks/useFeatureFlag";
import { Field, Form, Formik } from "formik";
import { FormikEffect } from "../FormikEffect";
import {
  getMetricById,
  getMetricName,
  getReportMetrics,
  isMetricEnabled,
  METRIC_ENTRY_TIMING,
} from "portal/utils/certifiedMetrics";
import { isEqual } from "portal/utils/equality";
import { isMetricNumber, Metric, MetricValidation } from "portal/utils/metrics";
import { isNewReport, secondsToRange } from "portal/utils/reports";
import { isNil, isUndefined } from "portal/utils/identity";
import { MetricHelp } from "portal/components/MetricHelp";
import { MetricInternalIcon } from "portal/components/MetricInternalIcon";
import { MetricUnitSpan } from "../measurement/Unit";
import { moveItem } from "portal/utils/arrays";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import {
  ReportMode,
  reportModeFromJSON,
  ReportResponse,
} from "protos/portal/reports";
import { ReportTools } from "portal/components/reports/ReportTools";
import { skipToken } from "@reduxjs/toolkit/query";
import { SortableContainer } from "portal/components/Sortable";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { SwitchWithLabel } from "../SwitchWithLabel";
import { TFunction } from "i18next";
import {
  useAuthorizationRequired,
  withAuthorizationRequired,
} from "../auth/WithAuthorizationRequired";
import { useDebounce } from "@uidotdev/usehooks";
import { useJigsawMetrics } from "portal/utils/hooks/useJigsawMetrics";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { values } from "portal/utils/objects";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import React, { FunctionComponent, useMemo, useState } from "react";
import RemoveIcon from "@mui/icons-material/RemoveOutlined";
import SaveIcon from "@mui/icons-material/SaveOutlined";

interface ReportEditorProps {
  report: ReportResponse;
  open?: boolean;
  onChange: (editingReport: ReportResponse) => void;
  onSave: (editingReport: ReportResponse) => void;
  onCancel: () => void;
}

const _ReportEditor: FunctionComponent<ReportEditorProps> = ({
  report,
  open = false,
  onChange,
  onSave,
  onCancel,
}) => {
  const navigate = useNavigate();
  const { user, customer } = useSelf();
  const useJigsaw = useJigsawMetrics();
  const { isEnabled: hasUnvalidatedMetrics } = useFeatureFlag(
    FeatureFlag.UNVALIDATED_METRICS
  );
  const { isEnabled: entryTimingEnabled } = useFeatureFlagForCustomer(
    FeatureFlag.ENTRY_TIMING,
    report.customerUuid
  );

  const canReadInternalMetrics = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.metrics_internal,
      PermissionDomain.all
    )
  );
  const canReadCustomers = useAuthorizationRequired(
    buildPermission(
      PermissionAction.read,
      PermissionResource.customers,
      PermissionDomain.all
    )
  );

  const { t } = useTranslation();

  const [deleteReport] = useMutationPopups(
    portalApi.endpoints.deleteReport.useMutation(),
    {
      success: t("utils.actions.deletedLong", {
        subject: capitalize(t("models.reports.report_one")),
      }),
    }
  );
  // customers are selected by uuid; this list (already fetched by the
  // CustomerSelector below) resolves the matching numeric id for consumers
  // that still work with ids
  const { data: allCustomers } = portalApi.endpoints.listCustomers.useQuery(
    canReadCustomers ? undefined : skipToken
  );
  // confirmation dialog
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  // columns
  const visibleColumns = useMemo<Metric[]>(() => {
    return report.visibleColumns
      .map((id) => getMetricById(id, report.mode))
      .filter(
        (metric): metric is Metric =>
          !isUndefined(metric) &&
          isMetricEnabled(metric.id, { entryTimingEnabled })
      );
  }, [entryTimingEnabled, report.mode, report.visibleColumns]);
  const hiddenColumns = useMemo<Metric[]>(
    () =>
      getReportMetrics(report.mode, {
        useJigsaw,
        entryTimingEnabled,
      }).filter(
        (metric) => !visibleColumns.some((visible) => visible.id === metric.id)
      ),
    [entryTimingEnabled, report, useJigsaw, visibleColumns]
  );

  const [_dragging, setDragging] = useState<Metric | undefined>(undefined);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // The grade stays on the saved report while the flag is hidden, including
  // while the flag is still loading. A column edit would otherwise drop it.
  const savedColumnIds = (columns: Metric[]): string[] => {
    const ids = columns.map((metric) => metric.id);
    const savedId = METRIC_ENTRY_TIMING.id;
    if (
      isMetricEnabled(savedId, { entryTimingEnabled }) ||
      ids.includes(savedId) ||
      !report.visibleColumns.includes(savedId)
    ) {
      return ids;
    }
    const at = report.visibleColumns.indexOf(savedId);
    return [...ids.slice(0, at), savedId, ...ids.slice(at)];
  };

  const setList = (list: Metric[]): void => {
    if (list === visibleColumns) {
      onChange({
        ...report,
        visibleColumns: savedColumnIds(list),
      });
    }
  };

  const handleDragStart = ({ active }: DragStartEvent): void => {
    const metric = getMetricById(active.id as string, report.mode);
    if (!metric) {
      return;
    }
    setDragging(metric);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    setList(
      moveItem(
        visibleColumns,
        active.data.current?.sortable.index,
        over?.data.current?.sortable.index
      )
    );

    setDragging(undefined);
  };

  const getRenderMetric =
    (t: TFunction, isHidden: boolean = false) =>
    (metric: Metric): JSX.Element => {
      if (
        isHidden &&
        metric.validation === MetricValidation.PENDING &&
        !hasUnvalidatedMetrics
      ) {
        return <></>;
      }
      if (
        isHidden &&
        metric.validation === MetricValidation.INTERNAL &&
        !canReadInternalMetrics
      ) {
        return <></>;
      }
      return (
        <li
          key={metric.id}
          className={classes(
            "group whitespace-nowrap flex items-center rounded-sm"
          )}
        >
          <button
            type="button"
            className="w-full flex items-center whitespace-nowrap border-0 bg-transparent text-inherit p-0"
            onClick={() => {
              const newColumns = isHidden
                ? [...visibleColumns, metric]
                : visibleColumns.filter(({ id }) => id !== metric.id);
              onChange({
                ...report,
                visibleColumns: savedColumnIds(newColumns),
              });
            }}
          >
            {isHidden ? (
              <IconButton className="h-6 w-6">
                <AddIcon />
              </IconButton>
            ) : (
              <IconButton className="h-6 w-6">
                <RemoveIcon />
              </IconButton>
            )}
            {metric.validation === MetricValidation.INTERNAL && (
              <MetricInternalIcon className="ml-1" />
            )}
            <strong
              className={classes("text-ellipsis ml-1 mr-1 text-base", {
                "cursor-row-resize": !isHidden,
                "cursor-pointer": isHidden,
              })}
            >
              {getMetricName(t, metric)}
            </strong>
            {isMetricNumber(metric) && <MetricUnitSpan metric={metric} />}
            <span className="inline-block grow" /* spacer */ />
            <MetricHelp metric={metric} />
          </button>
        </li>
      );
    };

  const canUpdateSelf = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.self
    )
  );
  const canUpdateCustomer = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.customer
    )
  );
  const canUpdateAll = useAuthorizationRequired(
    buildPermission(
      PermissionAction.update,
      PermissionResource.reports,
      PermissionDomain.all
    )
  );
  const canUpdate =
    (canUpdateSelf && (!report.authorId || report.authorId === user?.userId)) ||
    (canUpdateCustomer && report.customerId === customer?.db?.id) ||
    canUpdateAll;

  if (!open) {
    return;
  }

  return (
    <Drawer
      variant="permanent"
      anchor="right"
      classes={{
        paper: classes("h-auto p-4 bottom-0", "top-16", "w-screen md:w-80"),
      }}
    >
      <Formik
        enableReinitialize
        initialValues={{
          name: report.name,
          customerId: report.customerId,
          customerUuid: report.customerUuid,
          automateWeekly: report.automateWeekly,
          showAverage: report.showAverage,
          showTotal: report.showTotal,
          mode: report.mode || reportModeFromJSON(ReportMode.REPORT_MODE_DAYS),
          startDate: report.startDate,
          endDate: report.endDate,
          robotIds: report.robotIds,
        }}
        validationSchema={object({
          name: string().required(t("utils.form.required")),
          showAverage: boolean(),
          customerId: number()
            .transform((value) => (Number.isNaN(value) ? undefined : value))
            .nullable(),
          customerUuid: string(),
          automateWeekly: boolean(),
          showTotal: boolean(),
          mode: number().oneOf(values(ReportMode)),
          startDate: number(),
          endDate: number(),
          robotIds: array().of(number()),
        })}
        onSubmit={async (values) => {
          if (!canUpdate) {
            return;
          }
          // values carry a matched customerId/customerUuid pair, so the
          // backend resolves the same customer from either field
          await onSave({ ...report, ...values });
        }}
      >
        {({ submitForm, isSubmitting, values, setFieldValue }) => (
          <Form className={classes("flex flex-col gap-4")}>
            <FormikEffect
              onChange={(previous, next) => {
                if (
                  !previous?.values ||
                  !next.values ||
                  isEqual(previous.values, next.values)
                ) {
                  return;
                }
                onChange({
                  ...report,
                  ...next.values,
                });
              }}
            />
            <div className="flex justify-end gap-2">
              {canUpdate && (
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => setConfirmDelete(true)}
                  startIcon={<DeleteIcon />}
                >
                  {t("utils.actions.delete")}
                </Button>
              )}
              {confirmDelete && (
                <ConfirmationDialog
                  title={t("utils.actions.deleteLong", {
                    subject: t("models.reports.report_one"),
                  })}
                  description={t(
                    "components.ConfirmationDialog.delete.description",
                    {
                      subject: report.name,
                    }
                  )}
                  destructive
                  yesText={t("utils.actions.delete", {
                    subject: report.name,
                  })}
                  onClose={() => setConfirmDelete(false)}
                  onYes={async () => {
                    await deleteReport({ slug: report.slug });
                    navigate(Path.REPORTS);
                  }}
                />
              )}
              {canUpdate && (
                <>
                  <Button variant="text" onClick={() => onCancel()}>
                    {isNewReport(report)
                      ? t("utils.actions.discard")
                      : t("utils.actions.cancel")}
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={!report}
                    loading={isSubmitting}
                    onClick={submitForm}
                    startIcon={isNewReport(report) ? <AddIcon /> : <SaveIcon />}
                  >
                    {isNewReport(report)
                      ? t("utils.actions.create")
                      : t("utils.actions.save")}
                  </Button>
                </>
              )}
            </div>
            <Field
              component={TextField}
              label={t("views.reports.scheduled.editor.fields.name")}
              name="name"
              className="text-lg"
              disabled={!canUpdate}
            />
            <DuplicateNameAlert
              name={values.name}
              slug={isNewReport(report) ? undefined : report.slug}
            />
            {canReadCustomers && (
              <CustomerSelector
                valueKey="uuid"
                value={values.customerUuid || undefined}
                onChange={(customerUuid) => {
                  const selected = allCustomers?.find(
                    ({ uuid }) => uuid === customerUuid
                  );
                  setFieldValue("customerUuid", customerUuid ?? "");
                  setFieldValue("customerId", selected?.db?.id ?? 0);
                  setFieldValue("robotIds", []);
                }}
                readOnly={!canUpdate}
                small
              />
            )}
            {canReadCustomers && (
              <Field
                component={SwitchWithLabel}
                type="checkbox"
                name="automateWeekly"
                label={t(
                  "views.reports.scheduled.editor.fields.automateWeekly"
                )}
                disabled={!canUpdate}
              />
            )}
            {!values.automateWeekly && (
              <ReportTools
                dateRange={secondsToRange(values.startDate, values.endDate)}
                direction="vertical"
                customerIds={values.customerId ? [values.customerId] : []}
                selectedRobots={values.robotIds}
                onDateRangeChange={([startDate, endDate]) => {
                  setFieldValue("startDate", startDate?.toUnixInteger());
                  setFieldValue("endDate", endDate?.toUnixInteger());
                }}
                onSelectedRobotsChange={(robotIds) =>
                  setFieldValue("robotIds", robotIds)
                }
                disabled={!canUpdate}
              />
            )}
            <Divider />
            <div>
              <Field
                type="checkbox"
                component={CheckboxWithLabel}
                name="showAverage"
                Label={{
                  label: t(
                    "views.reports.scheduled.editor.fields.showAverages"
                  ),
                }}
                disabled={!canUpdate}
              />
              <Field
                type="checkbox"
                component={CheckboxWithLabel}
                name="showTotal"
                Label={{
                  label: t("views.reports.scheduled.editor.fields.showTotals"),
                }}
                disabled={!canUpdate}
              />
            </div>
            <Divider />
            <ButtonGroup variant="contained" className="w-full flex">
              <Button
                variant={
                  values.mode ===
                  reportModeFromJSON(ReportMode.REPORT_MODE_DAYS)
                    ? "contained"
                    : "outlined"
                }
                classes={{ root: "w-1/2" }}
                onClick={() => {
                  setFieldValue(
                    "mode",
                    reportModeFromJSON(ReportMode.REPORT_MODE_DAYS)
                  );
                }}
                disabled={!canUpdate}
              >
                {t("utils.units.dLong_other")}
              </Button>
              <Button
                variant={
                  values.mode ===
                  reportModeFromJSON(ReportMode.REPORT_MODE_JOBS)
                    ? "contained"
                    : "outlined"
                }
                classes={{ root: "w-1/2" }}
                onClick={() => {
                  setFieldValue(
                    "mode",
                    reportModeFromJSON(ReportMode.REPORT_MODE_JOBS)
                  );
                }}
                disabled={!canUpdate}
              >
                {t("models.jobs.job_other")}
              </Button>
            </ButtonGroup>
            <Divider />
            <Typography variant="subtitle2">
              {t("views.reports.scheduled.editor.columnsVisible")}
            </Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContainer
                id="visible"
                items={visibleColumns}
                render={getRenderMetric(t, false)}
              />
            </DndContext>
            <Typography variant="subtitle2">
              {t("views.reports.scheduled.editor.columnsHidden")}
            </Typography>
            <ul className="p-0 m-0 list-none">
              {hiddenColumns.map(getRenderMetric(t, true))}
            </ul>
          </Form>
        )}
      </Formik>
    </Drawer>
  );
};

const DuplicateNameAlert: FunctionComponent<{
  name: string;
  slug?: string;
}> = ({ name, slug }) => {
  const { t } = useTranslation();
  const debouncedName = useDebounce(name, 300);
  const reportsWithName = portalApi.endpoints.listReports.useQuery(
    {
      name: debouncedName,
    },
    {
      skip: !name,
    }
  );

  if (!name || isNil(reportsWithName.data)) {
    return;
  }
  const duplicateNameCount = reportsWithName.data.reports.filter(
    (report) => report.slug !== slug
  ).length;
  if (duplicateNameCount > 0) {
    return (
      <Alert severity="warning">
        {t("views.reports.scheduled.editor.duplicateNames", {
          count: duplicateNameCount,
        })}
      </Alert>
    );
  }
};

export const ReportEditor = withAuthorizationRequired(
  {
    or: [
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.self
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.customer
      ),
      buildPermission(
        PermissionAction.read,
        PermissionResource.reports,
        PermissionDomain.all
      ),
    ],
  },
  _ReportEditor
);
